import { motion } from "framer-motion";
import { Lock } from "lucide-react";

const W = 800;
const H = 400;
const EASE = { duration: 0.7, ease: [0.22, 1, 0.36, 1] };

/** Futuristic indexed-chart rendering of the two paths. Coordinates are % of the box so HTML overlays align. */
export const FloorChart = ({ pts, unit, base, maxYears, moves, tag }) => {
  const baseY = H * (1 - base / 100);
  const yOf = (lvl) => baseY - lvl * unit * (H / 100);
  const xOf = (i) => (i / maxYears) * W;
  const n = pts.length;

  const series = (key) =>
    Array.from({ length: maxYears + 1 }, (_, i) => {
      if (i === 0) return [0, baseY];
      const p = pts[Math.min(i, n) - 1];
      return [xOf(Math.min(i, n)), p ? yOf(p[key]) : baseY];
    });
  const line = (s) => s.map(([x, y], i) => `${i ? "L" : "M"} ${x} ${y}`).join(" ");
  const area = (s) => `${line(s)} L ${s[s.length - 1][0]} ${baseY} L 0 ${baseY} Z`;

  const mkt = series("market");
  const iul = series("iul");
  const endX = xOf(n);
  const endY = n ? yOf(pts[n - 1].iul) : baseY;

  return (
    <div className="absolute inset-0" data-testid="floor-chart-canvas">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
        <defs>
          <linearGradient id="fc-iul-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#29ABE2" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#29ABE2" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="fc-mkt-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="fc-iul-line" x1="0" x2="1">
            <stop offset="0%" stopColor="#4CC3F0" />
            <stop offset="100%" stopColor="#8fdcff" />
          </linearGradient>
          <filter id="fc-glow" x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* Grid */}
        {[0.12, 0.3, 0.48, 0.66, 0.86].map((f) => (
          <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="rgba(255,255,255,0.05)" vectorEffect="non-scaling-stroke" />
        ))}
        {Array.from({ length: maxYears + 1 }, (_, i) => (
          <line key={i} x1={xOf(i)} x2={xOf(i)} y1="0" y2={H} stroke="rgba(255,255,255,0.05)" vectorEffect="non-scaling-stroke" />
        ))}
        {/* Loss zone */}
        <rect x="0" y={baseY} width={W} height={H - baseY} fill="rgba(220,60,60,0.05)" />
        {/* Floor */}
        <line x1="0" x2={W} y1={baseY} y2={baseY} stroke="#29ABE2" strokeWidth="1.5" strokeDasharray="6 6" opacity="0.8" vectorEffect="non-scaling-stroke" />

        {/* Market */}
        <motion.path initial={false} animate={{ d: area(mkt) }} transition={EASE} fill="url(#fc-mkt-fill)" />
        <motion.path
          initial={false}
          animate={{ d: line(mkt) }}
          transition={EASE}
          fill="none"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* IUL */}
        <motion.path initial={false} animate={{ d: area(iul) }} transition={EASE} fill="url(#fc-iul-fill)" />
        <motion.path
          initial={false}
          animate={{ d: line(iul) }}
          transition={EASE}
          fill="none"
          stroke="#29ABE2"
          strokeWidth="10"
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity="0.45"
          filter="url(#fc-glow)"
          vectorEffect="non-scaling-stroke"
        />
        <motion.path
          initial={false}
          animate={{ d: line(iul) }}
          transition={EASE}
          fill="none"
          stroke="url(#fc-iul-line)"
          strokeWidth="3.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Points + lock badges (HTML overlays in % so they never distort) */}
      {pts.map((p, i) => {
        const x = (xOf(i + 1) / W) * 100;
        const yi = (yOf(p.iul) / H) * 100;
        const ym = (yOf(p.market) / H) * 100;
        const locked = moves[p.key].iul === 0;
        return (
          <div key={i} data-testid={`floor-year-${i + 1}`} className="contents">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, left: `${x}%`, top: `${ym}%` }}
              transition={{ ...EASE, opacity: { delay: 0.55, duration: 0.25 } }}
              className="absolute w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/70"
            />
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, left: `${x}%`, top: `${yi}%` }}
              transition={{ ...EASE, opacity: { delay: 0.55, duration: 0.25 } }}
              className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0B132B] ring-2 ring-[#4CC3F0] shadow-[0_0_14px_rgba(76,195,240,0.9)]"
            />
            {locked && (
              <motion.span
                initial={false}
                animate={{ left: `${x}%`, top: `${yi}%` }}
                transition={EASE}
                className="absolute -translate-x-1/2 -translate-y-[calc(100%+12px)] w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#0B132B]/90 backdrop-blur ring-2 ring-[#29ABE2] text-[#4CC3F0] flex items-center justify-center"
                style={{ animation: "iul-fade 0.5s ease-out 0.5s both" }}
                aria-label="Gains locked in"
              >
                <Lock size={13} />
              </motion.span>
            )}
          </div>
        );
      })}

      {/* Live endpoint */}
      <motion.span
        initial={false}
        animate={{ left: `${(endX / W) * 100}%`, top: `${(endY / H) * 100}%` }}
        transition={EASE}
        className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      >
        <span className="absolute inset-0 -m-3 rounded-full bg-[#29ABE2]/40 animate-ping" />
        <span className="block w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_18px_rgba(76,195,240,1)]" />
        {n > 0 && n < maxYears - 1 && (
          <span className="absolute left-5 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[#29ABE2] text-[#0B132B] text-[11px] sm:text-xs font-black tracking-[0.15em] uppercase px-2 py-0.5 hidden sm:block">
            {tag}
          </span>
        )}
      </motion.span>
    </div>
  );
};
